import { computed, toValue, type MaybeRefOrGetter } from 'vue';
import { useQueries, useQuery } from '@tanstack/vue-query';
import { useApi } from '@shopexperts/shared';
import type { City, CityWithCountry } from '@shopexperts/legacy-api';
import type { SmartFilterCityOption } from './useExpertFacets';

const STALE_MS = 30 * 60 * 1000;
const SEARCH_PAGE_SIZE = 20;

export function cityOptionLabel(city: Pick<City, 'name' | 'region'>): string {
  return city.region ? `${city.name}, ${city.region}` : city.name;
}

function toCityOption(city: CityWithCountry): SmartFilterCityOption {
  return { value: String(city.id), label: cityOptionLabel(city), country: city.country };
}

/** Labels for stored city ids, including cities that have no published expert and so are absent from the facet. */
export function useCitiesByIds(ids: MaybeRefOrGetter<readonly string[]>) {
  const api = useApi();
  const normalized = computed(() => [...new Set(toValue(ids).filter(id => /^\d+$/.test(id)))].sort());
  const enabled = computed(() => normalized.value.length > 0);

  const { data, isFetching } = useQuery({
    queryKey: computed(() => ['cities', 'by-ids', normalized.value.join(',')]),
    queryFn: () => api.cities.getCitiesByIds({ query: { ids: normalized.value.join(',') } }),
    enabled,
    staleTime: STALE_MS,
    refetchOnWindowFocus: false,
  });

  const options = computed<SmartFilterCityOption[]>(() => (data.value ?? []).map(toCityOption));
  const labelById = computed(() => new Map(options.value.map(option => [option.value, option.label])));

  return { options, labelById, isFetching };
}

/** Word-prefix city search run against each selected country; results are merged, largest cities first per country. */
export function useCitySearchAcrossCountries(
  countries: MaybeRefOrGetter<readonly string[]>,
  query: MaybeRefOrGetter<string>,
) {
  const api = useApi();
  const codes = computed(() => [...new Set(toValue(countries).map(code => code.trim().toUpperCase()).filter(code => code.length === 2))]);
  const search = computed(() => toValue(query).trim());

  const results = useQueries({
    queries: computed(() => codes.value.map(country => ({
      queryKey: ['countries', country, 'cities', search.value, 'first-page'],
      queryFn: () => api.cities.getCountryCities({
        params: { country },
        query: search.value ? { q: search.value, per_page: SEARCH_PAGE_SIZE } : { per_page: SEARCH_PAGE_SIZE },
      }),
      staleTime: STALE_MS,
      refetchOnWindowFocus: false,
    }))),
  });

  const options = computed<SmartFilterCityOption[]>(() => results.value.flatMap((result, index) => {
    const country = codes.value[index];
    return (result.data?.data ?? []).map(city => toCityOption({ ...city, country }));
  }));
  const isFetching = computed(() => results.value.some(result => result.isFetching));

  return { options, isFetching, countries: codes };
}
